import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import codes from "../countryList";
import { validatePhoneNumber } from "../phoneValidation";

const UpdateContactModal = ({
  show,
  handleClose,
  formData,
  handleFormChange,
  handleSubmit,
}) => {
  const [errors, setErrors] = useState({});
  const [countryCode, setCountryCode] = useState("91");
  const [altCountryCode, setAltCountryCode] = useState("91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [altPhoneNumber, setAltPhoneNumber] = useState("");

  // Helper function to parse phone number and extract country code
  const parsePhoneNumber = (fullPhoneNumber) => {
    if (!fullPhoneNumber) return { countryCode: "91", phoneNumber: "" };
    
    // Match patterns like (+91) 1234567890, (++91) 1234567890, or +91 1234567890
    const match = fullPhoneNumber.match(/\(?\+{1,2}(\d{1,4})\)?\s*(.+)/);
    if (match) {
      return {
        countryCode: match[1],
        phoneNumber: match[2].replace(/\s+/g, '') // Remove spaces
      };
    }
    
    // If no country code found, assume it's just a number
    return { countryCode: "91", phoneNumber: fullPhoneNumber };
  };

  useEffect(() => {
    if (!show) {
      setErrors({});
    } else {
      // Parse existing phone numbers when modal opens
      console.log("Parsing phone numbers:", {
        phone: formData.phone_number,
        altPhone: formData.alternate_phone_number
      });
      
      const parsedPhone = parsePhoneNumber(formData.phone_number);
      const parsedAltPhone = parsePhoneNumber(formData.alternate_phone_number);
      
      console.log("Parsed results:", {
        parsedPhone,
        parsedAltPhone
      });
      
      setCountryCode(parsedPhone.countryCode);
      setPhoneNumber(parsedPhone.phoneNumber);
      setAltCountryCode(parsedAltPhone.countryCode);
      setAltPhoneNumber(parsedAltPhone.phoneNumber);
    }
  }, [show, formData.phone_number, formData.alternate_phone_number]);

  const validateField = (name, value) => {
    let error = "";

    if (name === "first_name" && !value) {
      error = "First Name is required";
    } else if (name === "email_address") {
      if (!value) {
        error = "Email Address is required";
      } else if (
        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) ||
        /\.\./.test(value) || // Prevent consecutive dots
        /^\.|\.$/.test(value) // Prevent dots at the beginning or end
      ) {
        error = "Email Address is invalid";
      }
    } else if (name === "phone_number") {
      if (!value) {
        error = "Phone Number is required";
      } else {
        const phoneValidation = validatePhoneNumber(value, countryCode);
        if (!phoneValidation.isValid) {
          error = phoneValidation.errorMessage;
        }
      }
    } else if (name === "alternate_phone_number" && value) {
      const altPhoneValidation = validatePhoneNumber(value, altCountryCode);
      if (!altPhoneValidation.isValid) {
        error = altPhoneValidation.errorMessage;
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "phone_number") {
      // Only allow digits
      const digitsOnly = value.replace(/\D/g, '');
      setPhoneNumber(digitsOnly);
      // Update the formData with combined country code and phone number
      const combinedPhone = digitsOnly ? `(+${countryCode}) ${digitsOnly}` : "";
      handleFormChange({ target: { name, value: combinedPhone } });
      validateField(name, digitsOnly);
    } else if (name === "alternate_phone_number") {
      // Only allow digits
      const digitsOnly = value.replace(/\D/g, '');
      setAltPhoneNumber(digitsOnly);
      // Update the formData with combined country code and phone number
      const combinedPhone = digitsOnly ? `(+${altCountryCode}) ${digitsOnly}` : "";
      handleFormChange({ target: { name, value: combinedPhone } });
      validateField(name, digitsOnly);
    } else if (name === "countryCode") {
      setCountryCode(value);
      // Update the formData with new country code
      const combinedPhone = phoneNumber ? `(+${value}) ${phoneNumber}` : "";
      handleFormChange({ target: { name: "phone_number", value: combinedPhone } });
      // Revalidate phone number with new country code
      if (phoneNumber) {
        const phoneValidation = validatePhoneNumber(phoneNumber, value);
        setErrors((prevErrors) => ({
          ...prevErrors,
          phone_number: phoneValidation.isValid ? "" : phoneValidation.errorMessage,
        }));
      }
    } else if (name === "alt_countryCode") {
      setAltCountryCode(value);
      // Update the formData with new country code
      const combinedPhone = altPhoneNumber ? `(+${value}) ${altPhoneNumber}` : "";
      handleFormChange({ target: { name: "alternate_phone_number", value: combinedPhone } });
      // Revalidate alternate phone number with new country code
      if (altPhoneNumber) {
        const altPhoneValidation = validatePhoneNumber(altPhoneNumber, value);
        setErrors((prevErrors) => ({
          ...prevErrors,
          alternate_phone_number: altPhoneValidation.isValid ? "" : altPhoneValidation.errorMessage,
        }));
      }
    } else {
      handleFormChange(e);
      validateField(name, value);
    }
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.first_name) {
      validationErrors.first_name = "First Name is required";
    }
    if (!formData.email_address) {
      validationErrors.email_address = "Email Address is required";
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email_address) ||
      /\.\./.test(formData.email_address) || // Prevent consecutive dots
      /^\.|\.$/.test(formData.email_address)
    ) {
      validationErrors.email_address = "Email Address is invalid";
    }
    if (!phoneNumber) {
      validationErrors.phone_number = "Phone Number is required";
    } else {
      const phoneValidation = validatePhoneNumber(phoneNumber, countryCode);
      if (!phoneValidation.isValid) {
        validationErrors.phone_number = phoneValidation.errorMessage;
      }
    }
    if (altPhoneNumber) {
      const altPhoneValidation = validatePhoneNumber(altPhoneNumber, altCountryCode);
      if (!altPhoneValidation.isValid) {
        validationErrors.alternate_phone_number = altPhoneValidation.errorMessage;
      }
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      handleSubmit();
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          {formData.id ? "Update" : "Add"} Contact Information
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleFormSubmit}>
          <div className="mb-3">
            <label htmlFor="first_name" className="form-label">
              First Name
            </label>
            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select"
                name="salutation"
                style={{ width: "100px" }}
                onChange={handleInputChange}
                value={formData.salutation || "Mr"}
              >
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Ms">Ms</option>
                <option value="M/s">M/s</option>
                <option value="Dr">Dr</option>
              </select>
              <input
                type="text"
                className="form-control"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
              />
            </div>
            {errors.first_name && (
              <div className="text-danger">{errors.first_name}</div>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="last_name" className="form-label">
              Last Name
            </label>
            <input
              type="text"
              className="form-control"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
            />
            {errors.last_name && (
              <div className="text-danger">{errors.last_name}</div>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="email_address" className="form-label">
              Email Address
            </label>
            <input
              type="text"
              className="form-control"
              id="email_address"
              name="email_address"
              value={formData.email_address}
              onChange={handleInputChange}
            />
            {errors.email_address && (
              <div className="text-danger">{errors.email_address}</div>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="phone_number" className="form-label">
              Phone Number
            </label>
            <div className="d-flex align-items-center justify-content-between">
              <select
                className="form-select"
                name="countryCode"
                style={{ width: "150px" }}
                onChange={handleInputChange}
                value={countryCode}
              >
                <option data-countrycode="IN" value="91">
                  India (+91)
                </option>
                <option data-countrycode="GB" value="44">
                  UK (+44)
                </option>
                <option data-countrycode="US" value="1">
                  USA (+1)
                </option>
                <optgroup label="Other countries">
                  {codes.map((country) => (
                    <option
                      key={country.code}
                      data-countrycode={country.code}
                      value={country.dial_code.replace('+', '')}
                    >
                      {country.name} ({country.dial_code})
                    </option>
                  ))}
                </optgroup>
              </select>
              <input
                type="tel"
                className="form-control"
                id="phone_number"
                name="phone_number"
                value={phoneNumber}
                onChange={handleInputChange}
                maxLength="15"
                placeholder="Enter phone number"
              />
            </div>
            {errors.phone_number && (
              <div className="text-danger">{errors.phone_number}</div>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="alternate_phone_number" className="form-label">
              Alternate Phone Number
            </label>
            <div className="d-flex align-items-center justify-content-between">
              <select
                className="form-select"
                name="alt_countryCode"
                style={{ width: "150px" }}
                onChange={handleInputChange}
                value={altCountryCode}
              >
                <option data-countrycode="IN" value="91">
                  India (+91)
                </option>
                <option data-countrycode="GB" value="44">
                  UK (+44)
                </option>
                <option data-countrycode="US" value="1">
                  USA (+1)
                </option>
                <optgroup label="Other countries">
                  {codes.map((country) => (
                    <option
                      key={country.code}
                      data-countrycode={country.code}
                      value={country.dial_code.replace('+', '')}
                    >
                      {country.name} ({country.dial_code})
                    </option>
                  ))}
                </optgroup>
              </select>
              <input
                type="tel"
                className="form-control"
                id="alternate_phone_number"
                name="alternate_phone_number"
                value={altPhoneNumber}
                onChange={handleInputChange}
                maxLength="15"
                placeholder="Enter alternate phone number"
              />
            </div>
            {errors.alternate_phone_number && (
              <div className="text-danger">{errors.alternate_phone_number}</div>
            )}
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleFormSubmit}
          disabled={
            !formData.first_name ||
            !formData.email_address ||
            !formData.phone_number ||
            Object.values(errors).some((error) => error)
          }
        >
          {formData.id ? "Update" : "Add"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateContactModal;
